'use client';

import Image from 'next/image';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import Markdown from 'react-markdown';

import { classNames } from '@bytel/product-wall/helpers';
import {
    Align,
    Box,
    BoxContent,
    BoxHeader,
    Button,
    ButtonVariant,
    Checkbox,
    Column,
    Columns,
    DirectionEnum,
    Divider,
    FlexBox,
    FlexItem,
    GapSize,
    Icon,
    IconName,
    IconSize,
    Justify,
    Popover,
    PopoverDirection,
    Price,
    PriceLevel,
    Row,
    Rows,
    Sticker,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TitleMarkup,
    TypographyAlign,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { FaiType } from '@app-types/plan';

import { FaiDiscountCallout } from '@components/fai-discount-callout';
import { InlinePrice } from '@components/inline-price';

import { useMediaQuery } from '@hooks/use-media-query';

import { getFaiDisplayedPricing } from '@helpers/fai';
import {
    FAI_COMPARATOR_DESKTOP_LIMIT,
    FAI_COMPARATOR_MOBILE_LIMIT,
    FAI_COMPARATOR_SELECTION_BREAKPOINT,
    FaiComparatorSelectedOffer,
    FaiComparatorSelectionEventDetail,
    getStoredFaiComparatorSelection,
    updateFaiComparatorSelection,
} from '@helpers/fai-comparator';
import { capitalize } from '@helpers/wording';

import { CUSTOM_EVENTS } from '@constants/provider';

import AnniversaryMslSvg from '@assets/anniversary-MSL.svg';
import { CheckboxChangeEventHandler } from '@trilogy-ds/react/lib/components/checkbox/CheckboxProps';
import { OnClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

import { NuggetsList } from './nuggets-list';
import { SpeedOption } from './speed-option';

interface CtaType {
    color?: ButtonVariant;
    text: string;
    onClick?: (event: OnClickEvent | undefined) => void;
    disabled?: boolean;
    alternativeState?: ReactNode;
    href?: string;
}

interface RocCardProps {
    plan?: FaiType;
    cta?: CtaType;
    shadowless?: boolean;
    withBorder?: boolean;
    smallSticker?: boolean;
    hasSiblingHat?: boolean;
    showComparatorCheckbox?: boolean;
    showDetailsButton?: boolean;
}

export function RocCard({
    plan,
    cta,
    shadowless = false,
    withBorder = false,
    smallSticker = true,
    hasSiblingHat = false,
    showComparatorCheckbox = false,
    showDetailsButton = true,
}: Readonly<RocCardProps>) {
    const [selectedOffers, setSelectedOffers] = useState<FaiComparatorSelectedOffer[]>(() =>
        getStoredFaiComparatorSelection(),
    );
    const maxComparatorSelection = useMediaQuery(FAI_COMPARATOR_SELECTION_BREAKPOINT)
        ? FAI_COMPARATOR_MOBILE_LIMIT
        : FAI_COMPARATOR_DESKTOP_LIMIT;

    const { price, priceForever, sticker, appliedPromo, bigPromo } = useMemo(() => {
        if (!plan) {
            return { price: 0, priceForever: 0, sticker: null, appliedPromo: undefined, bigPromo: undefined };
        }

        const sticker = plan.stickers?.[0]?.label ? (
            <Sticker variant={plan.stickers?.[0]?.color} label={plan.stickers[0].label} small={smallSticker} />
        ) : null;

        return {
            ...getFaiDisplayedPricing(plan),
            sticker,
        };
    }, [plan, smallSticker]);

    const handleSelectionChange = (event: Event) => {
        const { detail } = event as CustomEvent<FaiComparatorSelectionEventDetail>;
        setSelectedOffers(detail.selectedOffers);
    };

    useEffect(() => {
        window.addEventListener(CUSTOM_EVENTS.FAI_COMPARATOR_SELECTION, handleSelectionChange as EventListener);

        return () => {
            window.removeEventListener(CUSTOM_EVENTS.FAI_COMPARATOR_SELECTION, handleSelectionChange as EventListener);
        };
    }, []);

    const isPlanSelected = useMemo(() => {
        if (!plan) {
            return false;
        }

        return selectedOffers.some(
            (offer) => offer.rangeNg === plan.rangeNg && offer.commercialSeries === plan.commercialSeries,
        );
    }, [plan, selectedOffers]);

    const isSelectionLimitReached = selectedOffers.length >= maxComparatorSelection && !isPlanSelected;

    if (!plan) {
        return null;
    }

    const isAnniversaryMSL =
        plan.commercialSeries === 'serie_speciale' && (plan.rangeNg === 'ultym' || plan.rangeNg === 'very_fit');

    const handleCheckboxChange: CheckboxChangeEventHandler = (event) => {
        const updatedSelection = updateFaiComparatorSelection(
            {
                rangeNg: plan.rangeNg,
                commercialSeries: plan.commercialSeries,
            },
            event.checkboxChecked,
        );

        setSelectedOffers(updatedSelection);
    };

    return (
        <Box
            shadowless={shadowless}
            active={withBorder}
            flat={withBorder}
            className={classNames('is-fullheight', plan.hat ? 'has-outline-secondary' : '')}
            headerOffset={hasSiblingHat && !plan.hat}
        >
            {plan.hat && (
                <BoxHeader className='is-justified-center'>
                    <Text level={TextLevels.ONE} marginless typo={TypographyAlign.TEXT_CENTERED}>
                        {plan.hat}
                    </Text>
                </BoxHeader>
            )}
            <BoxContent>
                <FlexBox fullheight gap={GapSize.FIVE} direction={DirectionEnum.COLUMN} justify={Justify.SPACE_BETWEEN}>
                    <FlexItem>
                        <FlexBox gap={GapSize.FIVE} direction={DirectionEnum.COLUMN} justify={Justify.SPACE_BETWEEN}>
                            <FlexBox gap={GapSize.TWO} direction={DirectionEnum.ROW} className='roc-title'>
                                <FlexItem>
                                    <Title
                                        level={TitleLevels.FOUR}
                                        markup={TitleMarkup.H2}
                                        marginless={isAnniversaryMSL}
                                    >
                                        {plan.name}
                                    </Title>
                                    {isAnniversaryMSL && (
                                        <Image
                                            src={AnniversaryMslSvg}
                                            alt='Maison Sarah Lavoine'
                                            width={240}
                                            height={20}
                                        />
                                    )}
                                    {plan.subtitle && (
                                        <Text marginless level={TextLevels.ONE}>
                                            {plan.subtitle}
                                        </Text>
                                    )}
                                </FlexItem>
                                <img
                                    alt={`Image de l'offre ${plan.name}`}
                                    aria-hidden='true'
                                    className='roc-image'
                                    src={plan.image}
                                />
                            </FlexBox>
                            <View flexable align={Align.CENTER} justify={Justify.CENTER} className='roc-sticker'>
                                {sticker}
                            </View>
                            <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.TWO}>
                                {price > 0 && (
                                    <View flexable justify={Justify.CENTER}>
                                        <Price
                                            level={PriceLevel.FOUR}
                                            align={Align.CENTER}
                                            amount={price}
                                            oldAmount={price < priceForever ? priceForever : undefined}
                                            period='mois'
                                        />
                                    </View>
                                )}
                                <FlexBox
                                    align={Align.CENTER}
                                    direction={DirectionEnum.COLUMN}
                                    className='roc-offer-info'
                                    gap={GapSize.ZERO}
                                >
                                    {appliedPromo && price !== priceForever && (
                                        <Text
                                            level={TextLevels.TWO}
                                            typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                            marginless
                                        >
                                            pendant {appliedPromo.duration} mois puis{' '}
                                            <InlinePrice
                                                amount={priceForever}
                                                period='mois'
                                                typo={[]}
                                                level={TextLevels.TWO}
                                            />
                                        </Text>
                                    )}
                                    {plan.obligationLabel && (
                                        <Text level={TextLevels.TWO} marginless>
                                            {capitalize(plan.obligationLabel)}
                                        </Text>
                                    )}
                                </FlexBox>
                            </FlexBox>
                            {bigPromo && (
                                <FaiDiscountCallout
                                    discountAmount={bigPromo.discount}
                                    discountDuration={appliedPromo?.duration}
                                />
                            )}
                            {cta && (
                                <View flexable justify={Justify.CENTER} align={Align.CENTER} className='roc-cta'>
                                    <CtaButton cta={cta} />
                                </View>
                            )}
                            {plan.speedOption && (
                                <FlexItem align={Align.CENTER}>
                                    <SpeedOption speedOption={plan.speedOption} />
                                </FlexItem>
                            )}
                            {(plan.nuggets?.length ?? 0) > 0 && (
                                <FlexItem align={Align.CENTER}>
                                    <NuggetsList plan={plan} />
                                </FlexItem>
                            )}
                        </FlexBox>
                    </FlexItem>
                    <FlexItem narrow>
                        <FlexBox gap={GapSize.FIVE} direction={DirectionEnum.COLUMN}>
                            {Boolean(plan.bottomSheet) && (
                                <Box shadowless flat padding='md' radius='sm'>
                                    <BoxContent>
                                        <FlexBox direction={DirectionEnum.ROW}>
                                            {Boolean(plan.bottomSheet?.icon) && (
                                                <FlexBox align={Align.CENTER}>
                                                    <Icon
                                                        name={plan.bottomSheet!.icon as IconName}
                                                        size={IconSize.SMALL}
                                                    />
                                                </FlexBox>
                                            )}
                                            <FlexBox align={Align.CENTER}>
                                                <Text level={TextLevels.THREE}>
                                                    <Markdown>{plan.bottomSheet?.title}</Markdown>
                                                </Text>
                                            </FlexBox>
                                        </FlexBox>
                                    </BoxContent>
                                </Box>
                            )}
                            {(plan.options?.length ?? 0) > 0 && (
                                <>
                                    <Divider iconName={IconName.PLUS} />
                                    <Columns mobile className='roc-options'>
                                        {plan.options
                                            ?.sort((a, b) => a.position - b.position)
                                            .slice(0, 3)
                                            .map((option) => {
                                                return (
                                                    <Column size={4} key={`$col-${plan.gencode}-${option.label}`}>
                                                        <Rows>
                                                            <Row className='is-paddingless is-flex is-centered'>
                                                                <img
                                                                    key={`img-${plan.gencode}-${option.label}`}
                                                                    width={66}
                                                                    alt={option.logo_alt}
                                                                    src={option.logo_url}
                                                                />
                                                            </Row>
                                                            <Row>
                                                                <Text marginless level={TextLevels.FOUR}>
                                                                    {option.label}
                                                                </Text>
                                                            </Row>
                                                        </Rows>
                                                    </Column>
                                                );
                                            })}
                                    </Columns>
                                </>
                            )}
                            {plan.detailsUrl && showDetailsButton && (
                                <Button
                                    variant={ButtonVariant.GHOST}
                                    fullwidth
                                    href={plan.detailsUrl}
                                    className='mt-auto is-marginless'
                                >
                                    Voir les détails
                                </Button>
                            )}
                            {showComparatorCheckbox && (
                                <View flexable justify={Justify.CENTER} className='p-small-mobile'>
                                    {isSelectionLimitReached ? (
                                        <Popover
                                            direction={PopoverDirection.BOTTOM}
                                            trigger={
                                                <Checkbox
                                                    name={`roc-card-${plan.rangeNg}-${plan.commercialSeries}`}
                                                    label='Ajouter au comparateur'
                                                    className='is-marginless'
                                                    disabled
                                                />
                                            }
                                        >
                                            Vous avez déjà sélectionné {maxComparatorSelection} offres.
                                            Désélectionnez-en une autre pour continuer.
                                        </Popover>
                                    ) : (
                                        <Checkbox
                                            name={`roc-card-${plan.rangeNg}-${plan.commercialSeries}`}
                                            value={`${plan.rangeNg}-${plan.commercialSeries}`}
                                            label='Ajouter au comparateur'
                                            checked={isPlanSelected}
                                            onChange={handleCheckboxChange}
                                        />
                                    )}
                                </View>
                            )}
                        </FlexBox>
                    </FlexItem>
                </FlexBox>
            </BoxContent>
        </Box>
    );
}

interface CtaButtonProps {
    cta: CtaType;
}

function CtaButton({ cta }: Readonly<CtaButtonProps>) {
    if (cta.alternativeState) {
        return cta.alternativeState;
    }

    return (
        <Button
            fullwidth
            variant={cta.color ?? ButtonVariant.CONVERSION}
            onClick={cta.onClick}
            disabled={cta.disabled}
            href={cta.href}
            markup={cta.href ? 'a' : 'button'}
            className='!is-marginless'
        >
            {cta.text}
        </Button>
    );
}
